import { isAuthCallbackPath, renderAuthCallback } from "./authCallback";
import { loadConfig } from "./config";
import { parseLinkContext, readFragmentSecret } from "./linkContext";
import { fetchInvitationPreview, PreviewRequestError } from "./supabaseClient";
import { detectPlatform, buildAppLink, buildStoreLink } from "./deepLinks";
import { toPageState } from "./pageState";
import { render, type RenderLinks } from "./render";
import type { PageState } from "./types";

// Orchestrates one render pass. Pulled out of main.ts (which just calls
// this against document.getElementById("app")) so it's testable without
// the entry point's top-level side effect firing on import.
export async function runApp(root: HTMLElement): Promise<void> {
  // The auth callback route is a completely separate concern from the
  // invitation flow below — checked and handled first, with an
  // unconditional return, so none of the invitation-parsing/preview
  // logic ever runs for this path and is never at risk of being altered
  // by it.
  if (isAuthCallbackPath(window.location.pathname)) {
    renderAuthCallback(root);
    return;
  }

  const { invitationId, roleHint } = parseLinkContext(
    window.location.pathname,
    window.location.search,
  );
  // Read exactly once, up front. From here on the only two places this
  // value is ever used again are buildAppLink() (a same-origin href
  // built entirely client-side) and the clipboard-copy handler inside
  // render.ts — never fetch(), never console.*, never an analytics call.
  const fragmentSecret = readFragmentSecret(window.location.hash);

  let config: ReturnType<typeof loadConfig> | null;
  try {
    config = loadConfig();
  } catch {
    config = null;
  }

  const platform = detectPlatform(navigator.userAgent);
  const links: RenderLinks = {
    appLink:
      config && invitationId
        ? buildAppLink(config.appLinkBase, invitationId, roleHint, fragmentSecret)
        : null,
    storeLink: config ? buildStoreLink(platform, config.appStoreUrl, config.playStoreUrl) : null,
    fragmentSecret,
  };

  if (!invitationId) {
    render(root, { kind: "invalid" }, links);
    return;
  }
  if (!config) {
    // Missing/broken client configuration is indistinguishable, from the
    // recipient's point of view, from "we couldn't verify this right
    // now" — and treating it as NETWORK ERROR rather than a distinct
    // state avoids ever surfacing internal configuration details to an
    // unauthenticated visitor.
    render(root, { kind: "network_error" }, links);
    return;
  }

  render(root, { kind: "loading" }, links);

  let state: PageState;
  try {
    const preview = await fetchInvitationPreview(
      config.supabaseUrl,
      config.supabaseAnonKey,
      invitationId,
    );
    state = toPageState(preview);
  } catch (err) {
    if (err instanceof PreviewRequestError) {
      state = { kind: "network_error" };
    } else {
      throw err;
    }
  }
  render(root, state, links);
}
